import { Period } from "revlock-webutils";

const debug = process.env.DEBUG_CODE == "true";

/**
 * A True 30/360 revenue plan method that will evenly distribute
 * the revenue for each month by treating each month as 30 days.
 *
 * See https://docs.google.com/spreadsheets/d/150OJFFdEt7_Ux2WjjXWGXiDl_gPQOOpgUDzqQuZjPPY/edit?gid=0#gid=0
 */
export default {
    buildPlan: function buildPlan(
        startDate,
        endDate,
        orderAmount,
        startDateInclusive,
        calendarConfig,
        salesOrder,
        endDateAdjustedForTerm = false
    ) {
        if (Period.isBefore(endDate, startDate)) {
            throw new Error(
                `Plan starts ${startDate} before it ends ${endDate}`
            );
        }

        if (startDateInclusive === undefined) {
            startDateInclusive = true;
        }

        // There are a LOT of special cases in this implementation.  So we have
        // kept a bunch of logging in here that is controlled by the debug
        // variable above.  The start & end dates passed to us can change
        // depending on all these special cases so we keep the original dates
        // for debugging purposes.
        const originalStartDate = startDate;
        const originalEndDate = endDate;

        // Break out the components of the start and end dates.
        let startDateComps = Period.toComponents(startDate);
        let startYear = parseInt(startDateComps.year);
        let startMonth = parseInt(startDateComps.month);
        let startDay = parseInt(startDateComps.day);
        let daysInStartMonth = parseInt(
            Period.getDaysInMonth(Number(startMonth), Number(startYear))
        );
        let startDayMax30 = Math.min(30, startDay);

        let endDateComps = Period.toComponents(endDate);
        let endYear = parseInt(endDateComps.year);
        let endMonth = parseInt(endDateComps.month);
        let endDay = parseInt(endDateComps.day);
        let endDayMax30 = Math.min(
            30,
            endDay + (startDateInclusive == true ? 1 : 0)
        );
        let daysInEndMonth = parseInt(
            Period.getDaysInMonth(Number(endMonth), Number(endYear))
        );
        let movedEndDay = false;

        // There is a special case when the starting date's day is the
        // same as the ending date's day when the day == 1.  In this case
        // we don't want to count the very first day for revenue.  For
        // example if its 1/1 - 2/1 then skip 1/1 and only count 29 days
        // of revenue for January and 1 day of revenue for February.
        if (
            startDay == endDay &&
            startDay != daysInStartMonth &&
            !startDateInclusive
        ) {
            if (debug) {
                console.log(
                    ">>>> Skipping start day due to startDateInclusive is " +
                        "false and start and end dates are on the same day " +
                        "of month: ",
                    (salesOrder && salesOrder.id) || "N/A",
                    startDate,
                    endDate
                );
            }

            // In this case we skip the start day.
            startDate = Period.addDays(startDate, 1);
            startDateComps = Period.toComponents(startDate);
            startMonth = parseInt(startDateComps.month);
            startDay = parseInt(startDateComps.day);
            startDayMax30 = Math.min(30, startDay);
        } else if (
            startDay == endDay &&
            startDateInclusive &&
            !endDateAdjustedForTerm
        ) {
            if (debug) {
                console.log(
                    ">>>> Skipping end day due to startDateInclusive is " +
                        "true and start and end dates are on the same day " +
                        "of month: ",
                    (salesOrder && salesOrder.id) || "N/A",
                    startDate,
                    endDate
                );
            }

            // In this case we move the end day back 1 day.
            endDate = Period.addDays(endDate, -1);
            endDateComps = Period.toComponents(endDate);
            endYear = parseInt(endDateComps.year);
            endMonth = parseInt(endDateComps.month);
            endDay = parseInt(endDateComps.day);
            endDayMax30 = Math.min(
                30,
                endDay + (startDateInclusive == true ? 1 : 0)
            );
            movedEndDay = true;
        }

        // Figure out the number of days to recognized revenue for.
        const numYears = endYear - startYear;
        const numMonths = endMonth - startMonth;
        const numDays = endDayMax30 - startDayMax30;
        let adjustedDays = parseInt(numDays);

        // Special cases.
        if (endMonth == 2 && endDay == 27 && startDay > 27) {
            // If the end date is 2/27 and the start day is 31, 30, 29, or 28
            // then treat the remaining days calc as 0.
            adjustedDays = 0;
        } else if (startMonth == 2 && startDay == 28 && endDay >= 28) {
            // If the start date is 2/28 and the end day is 31, 30, 29, or 28
            // then treat the remaining days calc as 0.
            adjustedDays = 0;
        } else if (endMonth == 2 && endDay == 28 && daysInEndMonth == 29) {
            adjustedDays = 0;
        } else if (
            startMonth == 2 &&
            startDay == 29 &&
            daysInStartMonth == 29 &&
            endDay >= 30
        ) {
            adjustedDays = 0;
        } else if (
            startMonth == endMonth &&
            startMonth == 2 &&
            endDay == daysInStartMonth
        ) {
            // When both the start and end dates is in Feb, treat 28 day as 30
            // and treat < 28 as they are.
            adjustedDays = 30;
        } else if (startDay == 1 && endMonth == 2 && endDay >= 28) {
            adjustedDays = 30;
        } else if (!startDateInclusive && startDay == endDay + 1) {
            adjustedDays = 0;
        } else if (
            startYear == endYear &&
            startMonth != endMonth &&
            endDay == daysInEndMonth
        ) {
            // Its 1st - 30th/31st so full month where its within the same
            // calendar year but different months like Jan - Dec.
            adjustedDays++;
        }

        // This is the real number of revenue recognition days to find what the
        // recognition per day would be.
        adjustedDays = Math.min(30, adjustedDays);
        let revDays = 360 * numYears + 30 * numMonths + adjustedDays;

        // We adjusted the end date prior to calling this method because this
        // is a term contract, so it must be divisable by 30 even days.
        if (endDateAdjustedForTerm == true) {
            revDays = Math.round(revDays / 30) * 30;
        }

        // This is the item's starting and ending period.
        const startActgPeriod = Period.toActgPeriod(startDate, calendarConfig);
        const endActgPeriod = Period.toActgPeriod(endDate, calendarConfig);

        // The accounting periods involved.
        const periods =
            startActgPeriod.period === endActgPeriod.period
                ? [startActgPeriod.period]
                : Period.periodsBetween(
                      startActgPeriod.period,
                      endActgPeriod.period,
                      true,
                      true,
                      calendarConfig || { type: "default" }
                  );

        // Force all months to 30 days so if the start date is on either the
        // 30th or 31st of the month consider both are just 1 day before start
        // of next period.  In addition if the start date is 2/28 and there is
        // a total of 28 days in February (or 29 days doesn't matter) then the
        // number of days before next period would be 3 because we are forcing
        // every month as 30 days.
        let numDaysBeforeStartOfNextPeriod =
            startDayMax30 >= 30 ||
            (startMonth == 2 && startDay == daysInStartMonth)
                ? 1
                : 30 - startDayMax30 + 1;

        if (startMonth == 2 && endDay <= 28 && startDay == daysInStartMonth) {
            if (startDay == endDay + 1 && movedEndDay == true) {
                // Some cases will have 2/28/26 - 3/28/26 (originally but we
                // moved 3/28 to 3/27), where the real start date was 29 for
                // the complete run, in this case we need to ensure the first
                // period is only 2 days and not 3.
                numDaysBeforeStartOfNextPeriod = 2;
            } else {
                // So for February we use the end date for this item to try and
                // determine what real start date this was.  For example say the
                // first item was 9/29-10/28, that would really be 2 days in the
                // first part of the month and 30-2=28 in the second part of the
                // month, but when we get to 2/28-3/28 we need to use the end
                // date here (3/28) to determine what the real number of days to
                // use for the first part, in this example we want 2/28 to really
                // be 2 days on the Feb side and 30-2=28 on the March side.
                numDaysBeforeStartOfNextPeriod = Math.min(
                    30 - startDay + 1,
                    30 - endDay
                );
            }
        }

        // Calculate the amount of revenue per day.
        const realNumOfPeriods = Math.max(1, Math.round(revDays / 30));
        const amountPerDay = orderAmount / parseFloat(revDays);

        if (debug) {
            console.log(
                ">>>> startActgPeriod: ",
                startDate,
                "(" +
                    originalStartDate +
                    "; " +
                    startDay +
                    "; " +
                    startDayMax30 +
                    ")",
                " => ",
                startActgPeriod.period
            );
            console.log(
                ">>>> endActgPeriod: ",
                endDate,
                "(" +
                    originalEndDate +
                    "; " +
                    endDay +
                    "; " +
                    endDayMax30 +
                    ")",
                " => ",
                endActgPeriod.period
            );
            console.log(
                ">>>> revDays: ",
                numYears,
                "yr",
                numMonths,
                "mo",
                numDays,
                "days",
                adjustedDays,
                " adjusted days = ",
                revDays
            );
            console.log(
                ">>>> numDaysBeforeStartOfNextPeriod: ",
                numDaysBeforeStartOfNextPeriod
            );
            console.log(">>>> daysInStartMonth: ", daysInStartMonth);
            console.log(
                ">>>> amountPerDay: ",
                amountPerDay,
                " for order amount: ",
                orderAmount,
                " with true number of rev days: ",
                revDays,
                " and real number of periods: ",
                realNumOfPeriods
            );
        }

        let runningAmount = 0.0;
        const plan = [];

        for (const period of periods) {
            let planAmount = 0;

            if (period === startActgPeriod.period) {
                // For the starting period which is a partial month
                // calculate based no a 30 day month.
                planAmount = numDaysBeforeStartOfNextPeriod * amountPerDay;

                if (realNumOfPeriods == 1 && startMonth == endMonth) {
                    // If there is only 1 period and the start and end months
                    // are the same then allocate the full order amount
                    planAmount = orderAmount;
                }
            } else if (period === endActgPeriod.period) {
                // Recognize the remainder of the amount in the last period.
                planAmount = orderAmount - runningAmount;
            } else {
                // For any month in between the starting and ending month
                // calculate the amount based on a 30 day month.
                planAmount = 30 * amountPerDay;
            }

            runningAmount += planAmount;
            plan.push({
                actgPeriod: Period.toActgPeriod(period, calendarConfig),
                planAmount
            });
        }

        if (debug) {
            console.log(
                ">>>> Plan: ",
                (salesOrder && salesOrder.id) || "N/A",
                plan.map((p) => {
                    return {
                        actgPeriod: p.actgPeriod.period,
                        planAmount: p.planAmount
                    };
                })
            );
        }

        // The plan is validated in ArrangementUtils so don't do it twice.
        return plan;
    }
};
